/**
 * Renderers for the typed blocks in notice.body.sections. One component per
 * block type, dispatched from RENDERERS; an unrecognised type falls through to
 * UnknownSection rather than blanking the notice.
 *
 * Every renderer gets the same `ctx` bundle, so adding an interactive block
 * type later touches neither the call site nor its sibling renderers.
 */
import React from "react";
import styles from "./Consent.module.css";
import { CONSENT } from "../../../Utils/Strings";
import { stringFormatter } from "../../../Utils/helper";
import { SECTION_TYPE } from "./consentAdapter";

export const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const hasValue = (entry) => entry.value !== null && entry.value !== undefined && entry.value !== "";

export const EntryGrid = ({ entries }) => {
  /* A request that lists field names without values is shown as tags: a
     label/value row with an em dash would read as "data missing" rather than
     "this is what has been asked for". */
  if (!entries.some(hasValue)) {
    return (
      <ul className={styles.tagList}>
        {entries.map((entry, index) => (
          <li className={styles.tag} key={`${entry.label}-${index}`}>{entry.label}</li>
        ))}
      </ul>
    );
  }

  return (
    <dl className={styles.entryGrid}>
      {entries.map((entry, index) => (
        <div className={styles.entry} key={`${entry.label}-${index}`}>
          <dt className={styles.entryLabel}>{entry.label || "—"}</dt>
          <dd className={styles.entryValue}>{displayValue(entry.value)}</dd>
        </div>
      ))}
    </dl>
  );
};

/* Rendered inside a caller-supplied <p> so a link between two text runs stays
   inline instead of breaking the sentence. */
const InlineRuns = ({ runs }) => (
  <>
    {runs.map((run, index) =>
      run.kind === "link" ? (
        <a
          key={index}
          className={styles.inlineLink}
          href={run.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {run.text}
        </a>
      ) : (
        <React.Fragment key={index}>{run.text}</React.Fragment>
      )
    )}
  </>
);

const Paragraphs = ({ paragraphs }) =>
  !paragraphs?.length ? null : (
    <div className={styles.prose}>
      {paragraphs.map((runs, index) => (
        <p className={styles.blockText} key={index}>
          <InlineRuns runs={runs} />
        </p>
      ))}
    </div>
  );

const Heading = ({ text }) => (text ? <h3 className={styles.sectionHeading}>{text}</h3> : null);

const ProseSection = ({ section }) => (
  <>
    <Heading text={section.heading} />
    <Paragraphs paragraphs={section.paragraphs} />
  </>
);

const BulletsSection = ({ section }) => (
  <>
    <Heading text={section.heading} />
    <ul className={styles.bulletList}>
      {section.items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  </>
);

const KeyValueSection = ({ section }) => (
  <>
    <Heading text={section.heading} />
    <Paragraphs paragraphs={section.paragraphs} />
    {section.entries.length > 0 && <EntryGrid entries={section.entries} />}
  </>
);

const RequiredMark = ({ mandatory }) =>
  mandatory ? (
    <span className={styles.requiredMark} title={CONSENT.REQUIRED_TAG} aria-hidden="true">*</span>
  ) : null;

const DeclarationSection = ({ section, ctx }) => (
  <>
    <Heading text={section.heading} />
    <Paragraphs paragraphs={section.paragraphs} />
    <ul className={styles.ackList}>
      {section.acknowledgements.map((ack) => (
        <li key={ack.key}>
          <label className={styles.ackRow}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={Boolean(ctx.ackConsents[ack.key])}
              onChange={(event) => ctx.toggleAck(ack.key, event.target.checked)}
              aria-label={stringFormatter(CONSENT.ACK_CHECK_ARIA, [ack.text])}
            />
            <span>
              {ack.text}
              <RequiredMark mandatory={ack.mandatory} />
            </span>
          </label>
        </li>
      ))}
    </ul>
  </>
);

const Cell = ({ cell }) => {
  if (!cell || cell.kind === "empty") return "—";
  if (cell.kind === "tags") {
    return (
      <ul className={`${styles.tagList} ${styles.cellTags}`}>
        {cell.tags.map((tag) => (
          <li className={styles.tag} key={tag.id}>{tag.label}</li>
        ))}
      </ul>
    );
  }
  return cell.text;
};

/* A real <table>; the stylesheet reflows it into one card per row below 768px
   using the data-label attributes, so there is a single checkbox input per
   purpose at every width. */
const ConsentTableSection = ({ section, ctx }) => {
  const { columns, consentColumnKey, rows } = section;

  return (
    <>
      <Heading text={section.heading || CONSENT.DATA_REQUESTED} />
      {rows.length === 0 ? (
        <p className={styles.blockText}>{CONSENT.TABLE_EMPTY}</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key} scope="col">{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  {columns.map((column) =>
                    column.key === consentColumnKey ? (
                      <td key={column.key} data-label={column.label}>
                        <label className={styles.cellCheck}>
                          <input
                            type="checkbox"
                            className={styles.checkbox}
                            checked={Boolean(ctx.purposeConsents[row.key])}
                            onChange={(event) => ctx.togglePurpose(row.key, event.target.checked)}
                            /* A bare checkbox in a cell reads as unlabelled to a
                               screen reader, and the mobile reflow drops table
                               semantics -- so name it explicitly. */
                            aria-label={stringFormatter(CONSENT.PURPOSE_CHECK_ARIA, [
                              row.label || column.label
                            ])}
                          />
                          <span className={styles.cellCheckText}>
                            {row.mandatory ? CONSENT.REQUIRED_TAG : CONSENT.OPTIONAL_TAG}
                          </span>
                        </label>
                      </td>
                    ) : (
                      <td key={column.key} data-label={column.label}>
                        <Cell cell={row.cells[column.key]} />
                      </td>
                    )
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

const UnknownSection = ({ section }) => (
  <>
    <Heading text={section.heading} />
    <Paragraphs paragraphs={section.paragraphs} />
    {section.items?.length > 0 && (
      <ul className={styles.bulletList}>
        {section.items.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    )}
  </>
);

const RENDERERS = {
  [SECTION_TYPE.PROSE]: ProseSection,
  [SECTION_TYPE.BULLETS]: BulletsSection,
  [SECTION_TYPE.KEY_VALUE]: KeyValueSection,
  [SECTION_TYPE.DATA_TABLE]: ConsentTableSection,
  [SECTION_TYPE.DECLARATION]: DeclarationSection
};

export const NoticeSection = ({ section, ctx }) => {
  
  const Renderer = RENDERERS[section.type] || UnknownSection;
  return (
    <section className={styles.noticeSection}>
      <Renderer section={section} ctx={ctx} />
    </section>
  );
};
